import { useState } from "react";
import { useBookings } from "./useBookings";
import { useBookingActions } from "./useBookingActions";
import { useToast } from "@/hooks/use-toast";
import { useServicePermission } from "@/hooks/useServicePermission";
import { supabase } from "@/integrations/supabase/client";
import { useBookingCompletion } from "./useBookingCompletion";
import { BookingCompletionData } from "@/components/booking/BookingCompletionDialog";

export const useAdminCalendar = (serviceId: string | undefined) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  const [isBlockingDialogOpen, setIsBlockingDialogOpen] = useState<boolean>(false);
  const [isModifyDialogOpen, setIsModifyDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isTravelCancelDialogOpen, setIsTravelCancelDialogOpen] = useState<boolean>(false);
  const [isBookingDetailsOpen, setIsBookingDetailsOpen] = useState<boolean>(false);
  const [selectedBlockedBooking, setSelectedBlockedBooking] = useState<any | null>(null);
  const [selectedTravelBlock, setSelectedTravelBlock] = useState<any | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const { toast } = useToast();

  const {
    bookings,
    loading,
    blockedTimes,
    availableTimeSlots,
    fetchBookings
  } = useBookings(serviceId, selectedDate);

  const { isOwner, isAdmin, canEdit, loading: permissionLoading } = useServicePermission(serviceId || "");
  
  // Define canManageBookings based on permissions
  const canManageBookings = isOwner || isAdmin;
  // For backward compatibility
  const isAuthorized = canManageBookings;

  // console.log("Admin calendar permissions:", { isOwner, isAdmin, canManageBookings });

  const {
    handleBookingAction: handleBookingActionFromHook,
    actionInProgress: bookingActionInProgress,
    progressValue: bookingProgressValue,
    showSuccessAnimation: bookingShowSuccessAnimation
  } = useBookingActions(serviceId, canManageBookings, fetchBookings);
  
  // Add booking completion hook
  const {
    handleCompleteBooking,
    actionInProgress: completionInProgress,
    progressValue: completionProgress,
    showSuccessAnimation: completionAnimation
  } = useBookingCompletion(serviceId, fetchBookings);

  // Combine action status from both booking actions and completion
  const actionInProgress = bookingActionInProgress || completionInProgress;
  const progressValue = bookingActionInProgress ? bookingProgressValue : completionProgress;
  const showSuccessAnimation = bookingActionInProgress ? bookingShowSuccessAnimation : completionAnimation;

  // Update the handleBookingAction signature to include the optional reason parameter
  const handleBookingAction = async (bookingId: number, status: 'accepted' | 'declined', reason?: string): Promise<boolean> => {
    return await handleBookingActionFromHook(bookingId, status, reason);
  };

  const handleTimeSlotSelect = (startTime: string) => {
    setSelectedTimeSlot(startTime);
    setIsBlockingDialogOpen(true);
  };

  const handleBlockTime = async (startTime: string, endTime: string): Promise<void> => {
    if (!canManageBookings) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to manage this service's calendar",
      });
      return;
    }

    // Convert selectedDate to a string in 'YYYY-MM-DD' format
    const selectedDateString = selectedDate.toISOString().slice(0, 10);

    // Combine the selected date with the start and end times
    const startDateTime = `${selectedDateString}T${startTime}:00`;
    const endDateTime = `${selectedDateString}T${endTime}:00`;

    try {
      // Use a single object instead of an array for the insert
      const { data, error } = await supabase
        .from('bookings')
        .insert({
          service_id: parseInt(serviceId || "0"),
          start_time: startDateTime,
          end_time: endDateTime,
          status: 'blocked',
        });

      if (error) {
        throw error;
      }

      fetchBookings();
      setIsBlockingDialogOpen(false);
      setSelectedTimeSlot("");
      toast({
        title: "Success",
        description: "Time blocked successfully",
      });
    } catch (error: any) {
      console.error("Error blocking time:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to block time",
      });
    }
  };

  const handleModifyBlock = async (startTime: string, endTime: string): Promise<void> => {
    if (!canManageBookings) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to manage this service's calendar",
      });
      return;
    }

    if (!selectedBlockedBooking) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No blocked booking selected",
      });
      return;
    }

    // Convert selectedDate to a string in 'YYYY-MM-DD' format
    const selectedDateString = selectedDate.toISOString().slice(0, 10);

    // Combine the selected date with the start and end times
    const startDateTime = `${selectedDateString}T${startTime}:00`;
    const endDateTime = `${selectedDateString}T${endTime}:00`;

    try {
      const { error } = await supabase
        .from('bookings')
        .update({
          start_time: startDateTime,
          end_time: endDateTime,
        })
        .eq('id', selectedBlockedBooking.id);

      if (error) {
        throw error;
      }

      fetchBookings();
      setIsModifyDialogOpen(false);
      setSelectedBlockedBooking(null);
      toast({
        title: "Success",
        description: "Blocked time modified successfully",
      });
    } catch (error: any) {
      console.error("Error modifying blocked time:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to modify blocked time",
      });
    }
  };

  const handleDeleteBlock = async (): Promise<void> => {
    if (!canManageBookings) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to manage this service's calendar",
      });
      return;
    }

    if (!selectedBlockedBooking) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No blocked booking selected",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', selectedBlockedBooking.id);

      if (error) {
        throw error;
      }

      fetchBookings();
      setIsDeleteDialogOpen(false);
      setSelectedBlockedBooking(null);
      toast({
        title: "Success",
        description: "Blocked time deleted successfully",
      });
    } catch (error: any) {
      console.error("Error deleting blocked time:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete blocked time",
      });
    }
  };

  const handleDeleteFromDialog = async (): Promise<void> => {
    setIsModifyDialogOpen(false);
    setIsDeleteDialogOpen(true);
  };

  const handleCancelTravelBlock = async (): Promise<void> => {
    if (!canManageBookings) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to manage this service's calendar",
      });
      return;
    }

    if (!selectedTravelBlock) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No travel block selected",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', selectedTravelBlock.id);

      if (error) {
        throw error;
      }

      fetchBookings();
      setIsTravelCancelDialogOpen(false);
      setSelectedTravelBlock(null);
      toast({
        title: "Success",
        description: "Travel block cancelled successfully",
      });
    } catch (error: any) {
      console.error("Error cancelling travel block:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to cancel travel block",
      });
    }
  };

  // Get service name for UI display
  const [serviceName, setServiceName] = useState<string>("");
  
  // Fetch service name when serviceId changes
  useState(() => {
    const fetchServiceName = async () => {
      if (serviceId) {
        try {
          const { data, error } = await supabase
            .from('services')
            .select('name')
            .eq('id', parseInt(serviceId))
            .single();
          
          if (data && !error) {
            setServiceName(data.name || "");
          }
        } catch (e) {
          console.error("Error fetching service name:", e);
        }
      }
    };
    
    fetchServiceName();
  });

  return {
    selectedDate,
    setSelectedDate,
    bookings,
    blockedTimes,
    availableTimeSlots,
    selectedTimeSlot,
    setSelectedTimeSlot,
    isBlockingDialogOpen,
    setIsBlockingDialogOpen,
    isModifyDialogOpen,
    setIsModifyDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isTravelCancelDialogOpen,
    setIsTravelCancelDialogOpen,
    loading,
    selectedBlockedBooking,
    setSelectedBlockedBooking,
    selectedTravelBlock,
    setSelectedTravelBlock,
    selectedBooking,
    setSelectedBooking,
    isBookingDetailsOpen,
    setIsBookingDetailsOpen,
    handleTimeSlotSelect,
    handleDeleteBlock,
    handleModifyBlock,
    handleBlockTime,
    handleBookingAction,
    handleDeleteFromDialog,
    handleCancelTravelBlock,
    handleCompleteBooking, // Explicitly include this in the return object
    isAuthorized,
    isOwner,
    canManageBookings,
    actionInProgress,
    progressValue,
    showSuccessAnimation,
    fetchBookings,
    serviceName
  };
};
